import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Menu, Text, Tooltip } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconX } from "@tabler/icons";

import CheckboxFilter from "../ideas/filters/types/CheckboxFilter";
import FiltersContext from "../ideas/filters/context/FiltersContext";
import SelectedReleasesList from "./SelectedReleasesList";
import {
  useReleases,
  useIdeaReleases,
  useProductItemReleases,
} from "../../hooks/useReleases";
import {
  RELEASE_FILTER_KEY,
  RELEASE_NO_TARGET_TOOLTIP,
} from "../../constants/releases";
import {
  buildReleaseOptions,
  groupReleaseOptions,
  toReleaseIds,
  withGroupTicks,
} from "../../utils/releaseOptions";
import { getApiErrorMessage } from "../../api/error-message";

/* ------------------------------------------------------------------ *
 * Isolated filters context
 *
 * `CheckboxFilter` reads its state through `useFiltersContextHelper`, which
 * walks up to the nearest `FiltersContext`. On the ideas page that is the
 * page-level provider — the one driving the actual list query. Editing a
 * row's releases through it would put every row's picker on shared
 * `checkedComponents` and, worse, push a filter into `activeFilters`.
 *
 * So the picker mounts its own provider. It is not a fork of the filter
 * machinery: `CheckboxFilter` itself is reused verbatim, it is just given a
 * scratch context to write into. Mantine unmounts a closed `Menu.Dropdown`,
 * so a closed row costs nothing.
 * ------------------------------------------------------------------ */

interface PickerScopeProps {
  selectedValues: string[];
  /**
   * Changing this re-seeds the scratch state from `selectedValues`. Bumped
   * after a rejected save so the tick that was optimistically applied goes
   * away, without remounting (which would drop the search box and the
   * expanded groups mid-interaction).
   */
  syncToken: string;
  children: ReactNode;
}

const ReleasePickerScope: React.FC<PickerScopeProps> = ({
  selectedValues,
  syncToken,
  children,
}) => {
  const [checkedComponents, setCheckedComponents] = useState<{
    [key: string]: any[];
  }>({ [RELEASE_FILTER_KEY]: selectedValues });
  const [childQuery, setChildQuery] = useState<any>({
    [RELEASE_FILTER_KEY]: "",
  });
  const [activeFilters, setActiveFilters] = useState<any[]>([]);
  const [childFiltersValue, setChildFiltersValue] = useState<{
    [key: string]: any[];
  }>({});
  const [operator, setOperator] = useState<any>(undefined);

  useEffect(() => {
    setCheckedComponents({ [RELEASE_FILTER_KEY]: selectedValues });
    // `selectedValues` is a fresh array each render, so it is deliberately not
    // a dependency; `syncToken` is its stable digest plus a revision counter,
    // and re-seeding on real changes only is the whole point.
  }, [syncToken]);

  const noop = useCallback(() => undefined, []);

  const value = useMemo(
    () =>
      ({
        openedMenuType: "idea",
        setOpenedMenuType: noop,
        activeFilters,
        setActiveFilters,
        // Pinned: this scope exists to render exactly one filter.
        selectedParentFilter: RELEASE_FILTER_KEY,
        setSelectedParentFilter: noop,
        checkedComponents,
        setCheckedComponents,
        childFiltersValue,
        setChildFiltersValue,
        parentQuery: "",
        setParentQuery: noop,
        childQuery,
        setChildQuery,
        operator,
        setOperator,
        numberValue: undefined,
        setNumberValue: noop,
        rangeDates: undefined,
        setRangeDates: noop,
        subPageKey: undefined,
        editMode: false,
        setEditMode: noop,
      }) as any,
    [
      activeFilters,
      checkedComponents,
      childFiltersValue,
      childQuery,
      operator,
      noop,
    ]
  );

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
};

/* ------------------------------------------------------------------ */

interface ReleaseAssignmentMenuProps {
  /**
   * Write to a product item's own set. Mutually exclusive with `ideaId`; with
   * neither, there is no target and the control renders disabled.
   */
  productItemId?: string;
  /**
   * Write to an idea's own set (`PUT /v1/ideas/:id/releases`). Takes precedence
   * over `productItemId` — the two are different sets, so a caller passing
   * both would be asking for an ambiguity that does not exist.
   */
  ideaId?: string;
  /** Current assignment on whichever target is set. */
  releaseIds: string[];
  /** Called with the saved set once the server (or mock) has accepted it. */
  onSaved?: (releaseIds: string[]) => void;
  /** Permission-driven. Renders the target, suppresses the dropdown. */
  readOnly?: boolean;
  dropdownWidth?: number;
  children: React.ReactElement;
}

/**
 * The group→release picker: a checkbox tree of release groups with their
 * releases nested beneath, with search — `CheckboxFilter`'s `release` branch,
 * under a pinned list of what is already assigned. The tree offers only what
 * can still be added; the pinned list is the only place an assignment is shown
 * and the only place it is removed.
 *
 * Saving is per toggle, the way `TagsCell` saves, and every write is a **full
 * replacement**: the endpoint takes the complete desired set and `[]` clears
 * it. Which endpoint depends on the target — an idea and a product item each
 * own their releases, and this never writes one through the other.
 */
export const ReleaseAssignmentMenu: React.FC<ReleaseAssignmentMenuProps> = ({
  productItemId,
  ideaId,
  releaseIds,
  onSaved,
  readOnly = false,
  dropdownWidth = 320,
  children,
}) => {
  const { releases, groups, releasesTruncated } = useReleases();
  const { setReleases: setProductItemReleases } = useProductItemReleases();
  const { setReleases: setIdeaReleases } = useIdeaReleases();
  const [opened, setOpened] = useState(false);
  const revisionRef = useRef(0);
  const [revision, setRevision] = useState(0);
  /** Serialises the assignment writes — see `persist`. */
  const writeQueueRef = useRef<Promise<void>>(Promise.resolve());
  /**
   * The last set handed to the server for the *current* target, saved or still
   * in flight. Cleared below when the target changes.
   */
  const lastRequestedRef = useRef<string[] | null>(null);

  const options = useMemo(
    () => buildReleaseOptions(releases, groups),
    [releases, groups]
  );

  /**
   * Two option lists, and they are not interchangeable: `treeOptions` is only
   * what the tree may still *offer*, while `options` stays the list every
   * mapping and resolution uses — `toReleaseIds` drops any value with no row in
   * the list it is given, so mapping a toggle through `treeOptions` would strip
   * every already-selected id out of a write that is a full replacement.
   */
  const treeOptions = useMemo(() => {
    const selected = new Set(
      (releaseIds || []).map((id) => String(id).toLowerCase())
    );
    return options.filter(
      (option) => !selected.has(String(option.value).toLowerCase())
    );
  }, [options, releaseIds]);

  const targetId = ideaId || productItemId;
  const disabled = readOnly || !targetId;

  /**
   * The dedupe in `persist` is per target, and React reuses this instance
   * across list rows and across a drawer that swaps its subject. Without this,
   * giving a second target exactly the set the first one already had matches
   * the stale ref and is never written.
   */
  useEffect(() => {
    lastRequestedRef.current = null;
  }, [targetId]);

  /**
   * A one-filter config, hand-built rather than read from `ideasFiltersConfig`
   * so the picker works whether or not the account's `customizations` doc
   * carries the ideas-list filter registration yet. `dbFieldPath` matches the
   * registered filter so the two stay recognisably the same field.
   */
  const filtersConfig = useMemo(
    () => ({
      idea: {
        [RELEASE_FILTER_KEY]: {
          name: RELEASE_FILTER_KEY,
          label: "Release",
          type: "checkbox",
          filterObject: "idea",
          dbFieldPath: "productItem.releaseIds",
          options: treeOptions,
        },
      },
      evidence: {},
    }),
    [treeOptions]
  );

  const selectedValues = useMemo(
    // `CheckboxFilter` lowercases every option value, so the seeded selection
    // has to be lowercased too or nothing shows as ticked.
    //
    // The selected ids are still seeded even though the tree renders no row for
    // them: `Checkbox.Group` derives every toggle from this array, so they are
    // what carries the rest of the set into `handleValueChange`.
    //
    // `withGroupTicks` against `treeOptions` therefore adds nothing today — a
    // group in the tree only holds unselected releases — but it is what keeps a
    // group tick a pure function of the rows actually rendered.
    () =>
      withGroupTicks(releaseIds || [], groupReleaseOptions(treeOptions)).map(
        (id) => String(id).toLowerCase()
      ),
    [releaseIds, treeOptions]
  );

  const syncToken = `${selectedValues.join("|")}#${revision}`;

  const persist = useCallback(
    (nextIds: string[]) => {
      if (!targetId) return;

      // Compared against the last set *sent*, not the last one saved: with the
      // writes queued below, the `releaseIds` prop lags a burst of ticks, and
      // comparing against it would skip a write that does change the outcome.
      const current = lastRequestedRef.current ?? releaseIds ?? [];
      const unchanged =
        current.length === nextIds.length &&
        current.every((id, index) => id === nextIds[index]);
      if (unchanged) return;
      lastRequestedRef.current = nextIds;

      const write = async () => {
        try {
          const updated = ideaId
            ? await setIdeaReleases(ideaId, nextIds)
            : await setProductItemReleases(targetId, nextIds);
          onSaved?.(updated?.releaseIds ?? nextIds);
        } catch (error) {
          // Any rejection — an id that no longer exists, a permission failure — arrives as a
          // 400 carrying the unified `{success, error:{code, message}}` envelope. Surface it
          // and roll the ticks back rather than leaving the UI claiming a state the server
          // refused. There is no group-shape rejection left: every group is multi-select.
          showNotification({
            title: "Could not update releases",
            color: "red",
            message: getApiErrorMessage(error),
            icon: <IconX size="1.1rem" />,
            autoClose: 2500,
          });
          lastRequestedRef.current = null;
          revisionRef.current += 1;
          setRevision(revisionRef.current);
        }
      };

      // Chained, not fired in parallel. Every toggle sends the *complete*
      // desired set, so two in flight are two full replacements racing: if the
      // earlier, smaller one lands second the server keeps it and the user's
      // last choice is silently dropped. Queuing preserves the click order;
      // the cost is that a burst of ticks saves one after another, which is
      // the point. `.then(write, write)` so a rejection does not strand the
      // queue — the catch above has already handled it.
      writeQueueRef.current = writeQueueRef.current.then(write, write);
    },
    [targetId, ideaId, releaseIds, setIdeaReleases, setProductItemReleases, onSaved]
  );

  /**
   * The assignments the picker cannot see. `useReleases` loads one capped page,
   * so on an account past that cap a release this item is assigned to may have
   * no row in `options` — and the write is a full replacement, so letting
   * `toReleaseIds` drop it would silently unassign it. Only passed when the
   * list is genuinely truncated: on a complete list an unresolved id is a
   * deleted release, which the server has already `$pull`ed and would reject.
   */
  const retainIds = useMemo(
    () => (releasesTruncated ? releaseIds || [] : []),
    [releasesTruncated, releaseIds]
  );

  const handleValueChange = useCallback(
    (values: string[] | null) => {
      // Strips the synthetic release-group rows a cascading tick leaves behind
      // and restores the canonical id casing. Mapped through the full `options`
      // — never `treeOptions` — see the split above.
      persist(toReleaseIds(values || [], options, retainIds));
    },
    [persist, options, retainIds]
  );

  const handleUnselect = useCallback(
    (releaseId: string) => {
      persist(
        (releaseIds || []).filter((id) => String(id) !== String(releaseId))
      );
    },
    [persist, releaseIds]
  );

  const target = disabled ? (
    <Tooltip
      label={!targetId ? RELEASE_NO_TARGET_TOOLTIP : "Read only"}
      multiline
      maw={280}
      withinPortal
    >
      <Box
        data-testid="release-control-disabled"
        sx={{ cursor: "default", width: "100%" }}
      >
        {children}
      </Box>
    </Tooltip>
  ) : (
    children
  );

  if (disabled) return target;

  return (
    <Menu
      position="bottom-start"
      shadow="sm"
      opened={opened}
      onChange={setOpened}
      width={dropdownWidth}
      withinPortal
    >
      <Menu.Target>{children}</Menu.Target>
      <Menu.Dropdown
        p={0}
        data-testid="release-picker"
        sx={{ zIndex: 10000, paddingTop: 5 }}
      >
        {options.length === 0 ? (
          <Box p={12}>
            <Text
              size="sm"
              component="a"
              href="/settings/releases"
              target="_blank"
              sx={{ color: "#5C5CEB", textDecoration: "none" }}
            >
              Create a release +
            </Text>
          </Box>
        ) : (
          <>
            <SelectedReleasesList
              releaseIds={releaseIds || []}
              options={options}
              onUnselect={handleUnselect}
            />
            <ReleasePickerScope
              selectedValues={selectedValues}
              syncToken={syncToken}
            >
              <CheckboxFilter
                initialfilters={filtersConfig.idea as any}
                MenuType="idea"
                ideasFiltersConfig={filtersConfig}
                setOpened={setOpened}
                setValue={handleValueChange}
                hideAddButton
                clearLabel="Clear releases"
                resetFilters={() => {
                  // `[]` is the documented way to clear — the endpoint is a full
                  // replacement, so there is no "remove" call to make.
                  setOpened(false);
                  persist([]);
                }}
              />
            </ReleasePickerScope>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default ReleaseAssignmentMenu;
